import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { makeRequest } from "../../../utils/makeRequest";

// Standalone, no-auth booking page meant to be embedded in a third-party
// site via <iframe>. Talks only to the public /api/external/booking/*
// endpoints - no session, no facility staff login.
//
// A company usually has several facilities, each with its own listed
// units, and all of them should show up together on one page - so this
// widget takes ?company_id=... and lists across every facility under it.
// It also still accepts a single ?facility_id=... for embedding just one
// facility's units. Every returned property carries its own facility_id,
// which is what all subsequent calls (availability, pricing, booking,
// payment) must be made against - not the query param.
const PAYMENT_API_BASE_URL = process.env.REACT_APP_PAYMENT_API_URL || "https://sandbox.payments.payserve.co.ke";

const STEP = {
  LIST: 1,
  DATES: 2,
  GUEST_INFO: 3,
  PAYMENT: 4
};

const formatMoney = (amount, currency) => `${currency || "KES"} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Kenyan subscriber numbers are always 9 significant digits regardless of
// how they arrive (leading 0, 254 prefix, doubled prefix, stray "+", etc).
// Taking the last 9 digits and re-prepending 254 is idempotent and
// self-corrects malformed input instead of relying on fragile prefix
// stripping.
const formatMpesaPhone = (phone) => {
  const digits = (phone || "").replace(/\D/g, "");
  return "254" + digits.slice(-9);
};

const BookingWidget = () => {
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get("company_id");
  const singleFacilityId = searchParams.get("facility_id");

  const [step, setStep] = useState(STEP.LIST);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);

  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [availability, setAvailability] = useState(null);

  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");

  const [booking, setBooking] = useState(null);
  const [mpesaPhone, setMpesaPhone] = useState("");
  const [paymentStatus, setPaymentStatus] = useState(null); // null | 'pending' | 'paid' | 'failed'

  useEffect(() => {
    if (!companyId && !singleFacilityId) {
      setError("This booking widget is missing a company_id (or facility_id) parameter and cannot load.");
      return;
    }

    const fetchProperties = async () => {
      setLoading(true);
      setError("");

      const url = companyId
        ? `/api/external/booking/company-properties?company_id=${companyId}`
        : `/api/external/booking/properties?facility_id=${singleFacilityId}`;

      const response = await makeRequest(url, "GET");
      setLoading(false);

      if (response.success && response.data?.success) {
        setProperties(response.data.data || []);
      } else {
        setError("Unable to load available properties. Please try again later.");
      }
    };

    fetchProperties();
  }, [companyId, singleFacilityId]);

  const selectUnit = (property, unit) => {
    setSelectedProperty(property);
    setSelectedUnit(unit);
    setAvailability(null);
    setStep(STEP.DATES);
  };

  const handleCheckAvailability = async () => {
    if (!checkIn || !checkOut) {
      setError("Please select both check-in and check-out dates.");
      return;
    }

    setLoading(true);
    setError("");

    const response = await makeRequest("/api/external/booking/check-availability", "POST", {
      facility_id: selectedProperty.facility_id,
      property_id: selectedProperty.property_id,
      unit_id: selectedUnit.unit_id,
      check_in_date: checkIn,
      check_out_date: checkOut,
      guests: { adults, children }
    });

    setLoading(false);

    if (response.success && response.data?.success && response.data.available) {
      setAvailability(response.data.data);
      setError("");
    } else {
      setAvailability(null);
      setError(response.data?.message || "This unit is not available for the selected dates. Please try different dates.");
    }
  };

  const handleContinueToGuestInfo = () => {
    if (!availability) return;
    setStep(STEP.GUEST_INFO);
  };

  const handleCreateBooking = async () => {
    if (!guestName || !guestEmail || !guestPhone) {
      setError("Please fill in your name, email, and phone number.");
      return;
    }

    setLoading(true);
    setError("");

    const response = await makeRequest("/api/external/booking/create", "POST", {
      facility_id: selectedProperty.facility_id,
      property_id: selectedProperty.property_id,
      unit_id: selectedUnit.unit_id,
      check_in_date: checkIn,
      check_out_date: checkOut,
      guest_info: {
        name: guestName,
        email: guestEmail,
        phone: formatMpesaPhone(guestPhone).replace(/^254/, "+254"),
        adults,
        children
      },
      special_requests: specialRequests
    });

    setLoading(false);

    if (response.success && response.data?.success) {
      setBooking(response.data.data);
      setMpesaPhone(guestPhone);
      setStep(STEP.PAYMENT);
    } else {
      setError(response.data?.message || "We couldn't create your booking. Please try again.");
    }
  };

  const pollBookingStatus = (bookingReference, attemptsLeft = 20) => {
    if (attemptsLeft <= 0) {
      setPaymentStatus((current) => (current === "paid" ? current : "failed"));
      return;
    }

    setTimeout(async () => {
      const response = await makeRequest(
        `/api/external/booking/${bookingReference}/status?facility_id=${selectedProperty.facility_id}`,
        "GET"
      );

      const paymentStatusValue = response.data?.data?.payment?.payment_status;
      if (paymentStatusValue === "Paid") {
        setPaymentStatus("paid");
        return;
      }

      pollBookingStatus(bookingReference, attemptsLeft - 1);
    }, 4000);
  };

  const handlePayWithMpesa = async () => {
    if (!booking?.account_number) {
      setError("This booking has no M-Pesa account registered - please contact support.");
      return;
    }

    const formattedPhone = formatMpesaPhone(mpesaPhone);
    if (!/^254\d{9}$/.test(formattedPhone)) {
      setError("Please enter a valid Safaricom number, e.g. 0712345678.");
      return;
    }

    setLoading(true);
    setError("");
    setPaymentStatus("pending");

    try {
      await axios.post(`${PAYMENT_API_BASE_URL}/v1/stkpush`, {
        phone: formattedPhone,
        amount: parseInt(booking.pricing.total_amount, 10),
        accountNumber: booking.account_number,
        facilityId: selectedProperty.facility_id,
        customerId: `guest-${booking.booking_reference}`,
        uniqueIdentifier: booking.booking_reference
      });

      pollBookingStatus(booking.booking_reference);
    } catch (mpesaError) {
      setPaymentStatus("failed");
      setError("Failed to send the M-Pesa STK push. Please check the phone number and try again.");
    } finally {
      setLoading(false);
    }
  };

  if (error && step === STEP.LIST && properties.length === 0 && !loading) {
    return (
      <div className="container py-5 text-center">
        <p className="text-danger">{error}</p>
      </div>
    );
  }

  return (
    <div className="container py-4" style={{ maxWidth: "720px" }}>
      {error && <div className="alert alert-danger">{error}</div>}

      {step === STEP.LIST && (
        <div>
          <h4 className="mb-3">Available Units</h4>
          {loading && <p>Loading...</p>}
          {!loading && properties.length === 0 && <p className="text-muted">No units are currently available for booking.</p>}
          <div className="row g-3">
            {properties.map((property) => (
              <div className="col-12" key={property.property_id}>
                <div className="card">
                  {property.images?.length > 0 && (
                    <img
                      src={(property.images.find((img) => img.isPrimary) || property.images[0]).url}
                      alt={property.property_name}
                      className="w-100"
                      style={{ maxHeight: "220px", objectFit: "cover" }}
                    />
                  )}
                  <div className="card-body">
                    <h5 className="card-title">{property.property_name}</h5>
                    <p className="card-text text-muted mb-2">{property.description}</p>
                    <p className="fw-bold mb-3">{formatMoney(property.base_price, property.currency)} / night</p>
                    {property.units.map((unit) => (
                      <button
                        key={unit.unit_id}
                        type="button"
                        className="btn btn-primary me-2 mb-2"
                        onClick={() => selectUnit(property, unit)}
                      >
                        Book {unit.unit_name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {step === STEP.DATES && (
        <div>
          <button type="button" className="btn btn-link px-0 mb-2" onClick={() => setStep(STEP.LIST)}>&larr; Back</button>
          <h4 className="mb-3">{selectedProperty.property_name} - {selectedUnit.unit_name}</h4>
          <div className="row g-3">
            <div className="col-sm-6">
              <label className="form-label">Check-in</label>
              <input type="date" className="form-control" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} min={new Date().toISOString().split("T")[0]} />
            </div>
            <div className="col-sm-6">
              <label className="form-label">Check-out</label>
              <input type="date" className="form-control" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} min={checkIn || new Date().toISOString().split("T")[0]} />
            </div>
            <div className="col-sm-6">
              <label className="form-label">Adults</label>
              <input type="number" className="form-control" min={1} value={adults} onChange={(e) => setAdults(parseInt(e.target.value, 10) || 1)} />
            </div>
            <div className="col-sm-6">
              <label className="form-label">Children</label>
              <input type="number" className="form-control" min={0} value={children} onChange={(e) => setChildren(parseInt(e.target.value, 10) || 0)} />
            </div>
          </div>

          <button type="button" className="btn btn-primary mt-3" onClick={handleCheckAvailability} disabled={loading}>
            {loading ? "Checking..." : "Check Availability"}
          </button>

          {availability && (
            <div className="alert alert-success mt-3">
              <p className="mb-1">Available for {availability.nights} night(s).</p>
              <p className="mb-1 fw-bold">Total: {formatMoney(availability.pricing.total_amount, availability.pricing.currency)}</p>
              <button type="button" className="btn btn-success mt-2" onClick={handleContinueToGuestInfo}>Continue</button>
            </div>
          )}
        </div>
      )}

      {step === STEP.GUEST_INFO && (
        <div>
          <button type="button" className="btn btn-link px-0 mb-2" onClick={() => setStep(STEP.DATES)}>&larr; Back</button>
          <h4 className="mb-3">Your Details</h4>
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label">Full Name</label>
              <input type="text" className="form-control" value={guestName} onChange={(e) => setGuestName(e.target.value)} />
            </div>
            <div className="col-sm-6">
              <label className="form-label">Email</label>
              <input type="email" className="form-control" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} />
            </div>
            <div className="col-sm-6">
              <label className="form-label">Phone (M-Pesa number)</label>
              <input type="text" className="form-control" placeholder="0712345678" value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} />
            </div>
            <div className="col-12">
              <label className="form-label">Special Requests (optional)</label>
              <textarea className="form-control" rows={2} value={specialRequests} onChange={(e) => setSpecialRequests(e.target.value)} />
            </div>
          </div>

          <button type="button" className="btn btn-primary mt-3" onClick={handleCreateBooking} disabled={loading}>
            {loading ? "Booking..." : "Reserve Now"}
          </button>
        </div>
      )}

      {step === STEP.PAYMENT && booking && (
        <div>
          <h4 className="mb-3">Complete Your Payment</h4>
          <div className="alert alert-info">
            <p className="mb-1">Booking Reference: <strong>{booking.booking_reference}</strong></p>
            <p className="mb-0">Amount Due: <strong>{formatMoney(booking.pricing.total_amount, booking.pricing.currency)}</strong></p>
          </div>

          {paymentStatus === "paid" ? (
            <div className="alert alert-success">
              <i className="ti ti-check me-2"></i>
              Payment received! Your booking is confirmed. A confirmation email has been sent to {guestEmail}.
            </div>
          ) : (
            <>
              <label className="form-label">M-Pesa Phone Number</label>
              <input type="text" className="form-control mb-3" value={mpesaPhone} onChange={(e) => setMpesaPhone(e.target.value)} placeholder="0712345678" />

              <button type="button" className="btn btn-success" onClick={handlePayWithMpesa} disabled={loading || paymentStatus === "pending"}>
                {paymentStatus === "pending" ? "Waiting for payment..." : "Pay with M-Pesa"}
              </button>

              {paymentStatus === "pending" && (
                <p className="text-muted mt-2">Please check your phone and enter your M-Pesa PIN to complete the payment.</p>
              )}
              {paymentStatus === "failed" && (
                <p className="text-danger mt-2">We didn't receive confirmation of your payment in time. If you completed it on your phone, please contact support with your booking reference.</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingWidget;
